import React, {useEffect, useState} from 'react';
import {GRID_DETAIL_PANEL_TOGGLE_FIELD, DataGridPro} from '@mui/x-data-grid-pro';
import {Formik} from 'formik';
import {Box, Button, TextField} from '@mui/material';
import {useReportPackDashboard} from './useReportPackDashboard';
import {reportPackDashboardColumns} from './ReportPackDashboardColumns';
import {Panel} from '../../components/Panel/Panel';
import {themeColors} from '../../theme/Theme';
import useTitle from '../../hooks/useTitle';
import {useDebounce} from '../../hooks/useDebounce';
import ReportPackDashboardFiltersModal from './ReportPackDashboardFiltersModal';
import ReportPackDashboardStatusFiltersModal from './ReportPackDashboardStatusFiltersModal';
import {
  reportPackDashboardPageInitialValues,
  reportPackStatusKeys,
} from './ReportPackDashboardTypes';
import {useAllAdvisers} from '../../hooks/useAllAdvisers';

export const ReportPackDashboardPage: React.FC = () => {
  const {searchReportPacks, data, loading} = useReportPackDashboard();
  const {allAdvisers} = useAllAdvisers();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useTitle(`Report Pack Dashboard`);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isStatusFilterModalOpen, setIsStatusFilterModalOpen] = useState(false);
  const [formikValues, setFormikValues] = useState(reportPackDashboardPageInitialValues);

  useEffect(() => {
    if (debouncedSearchTerm !== formikValues.search) {
      searchReportPacks({
        variables: {
          contactNameIncludes: debouncedSearchTerm,
          adviserIds: formikValues.adviserIds,
          statuses: formikValues.statuses,
        },
      });
    }
  }, [
    debouncedSearchTerm,
    formikValues.search,
    formikValues.adviserIds,
    formikValues.statuses,
    searchReportPacks,
  ]);

  return (
    <Formik initialValues={reportPackDashboardPageInitialValues} onSubmit={() => {}}>
      {({setFieldValue, handleSubmit, values}) => {
        const {search} = values;

        if (JSON.stringify(values) !== JSON.stringify(formikValues)) {
          setFormikValues(values);
        }

        return (
          <form onSubmit={handleSubmit}>
            <Panel
              title="Report Pack Dashboard"
              style={{
                height: '100%',
              }}
              color={themeColors.lightAlert}
            >
              <Box display="flex" alignItems="baseline" flexWrap="wrap">
                <TextField
                  value={search}
                  autoFocus
                  onChange={(event) => {
                    const searchValue = event.target.value;
                    setFieldValue('search', searchValue);
                    setSearchTerm(searchValue);
                  }}
                  placeholder="Search reports"
                  style={{minWidth: 400}}
                  margin="normal"
                  inputProps={{
                    type: 'search',
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{marginLeft: 2}}
                  onClick={async () => {
                    const hasFilters =
                      values.statuses.length > 0 || values.adviserIds.length > 0;

                    if (hasFilters) {
                      setFieldValue('statuses', []);
                      setFieldValue('adviserIds', []);

                      await searchReportPacks({
                        variables: {
                          contactNameIncludes: values.search,
                          adviserIds: [],
                          statuses: [],
                        },
                      });
                    } else {
                      const allStatusValues = reportPackStatusKeys;
                      setFieldValue('statuses', allStatusValues);

                      const allAdviserIds = allAdvisers
                        .map((a) => String(a.id || ''))
                        .filter(Boolean);
                      setFieldValue('adviserIds', allAdviserIds);

                      await searchReportPacks({
                        variables: {
                          contactNameIncludes: values.search,
                          adviserIds: allAdviserIds,
                          statuses: allStatusValues,
                        },
                      });
                    }
                  }}
                >
                  {values.statuses.length > 0 || values.adviserIds.length > 0
                    ? 'Clear all filters'
                    : 'Select all statuses and advisers'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{marginLeft: 2}}
                  onClick={() => {
                    setIsStatusFilterModalOpen(true);
                  }}
                >
                  Filter By Status{' '}
                  {values.statuses.length ? `(${values.statuses.length})` : ''}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{marginLeft: 2}}
                  onClick={() => {
                    setIsFilterModalOpen(true);
                  }}
                >
                  Filter By Adviser{' '}
                  {values.adviserIds.length ? `(${values.adviserIds.length})` : ''}
                </Button>
              </Box>
              <DataGridPro
                initialState={{
                  columns: {
                    columnVisibilityModel: {
                      [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false, // This hides the expanding widget
                    },
                  },
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-menuIcon': {
                    display: 'none',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    '&:hover .MuiDataGrid-menuIcon': {
                      display: 'none',
                    },
                  },
                }}
                hideFooterRowCount
                loading={loading}
                columns={reportPackDashboardColumns}
                rows={(values.statuses.length > 0 ? data || [] : [])
                  .filter((row): row is NonNullable<typeof row> => row !== null)
                  .map((row, index) => ({
                    ...row,
                    id: row.id || `row-${index}`,
                  }))}
                rowHeight={60}
                density="comfortable"
                onSortModelChange={(model) => {
                  console.log('Sort model changed:', model);
                }}
                sortingOrder={['asc', 'desc']}
              />
            </Panel>
            {isFilterModalOpen && (
              <ReportPackDashboardFiltersModal
                isOpen={isFilterModalOpen}
                close={() => {
                  setIsFilterModalOpen(false);
                }}
                fetch={searchReportPacks}
              />
            )}
            {isStatusFilterModalOpen && (
              <ReportPackDashboardStatusFiltersModal
                isOpen={isStatusFilterModalOpen}
                close={() => {
                  setIsStatusFilterModalOpen(false);
                }}
                fetch={searchReportPacks}
              />
            )}
          </form>
        );
      }}
    </Formik>
  );
};
